import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { hideToast } from '../store/slices/notificationsSlice';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

const ToastNotifications = () => {
  const dispatch = useDispatch();
  const toasts = useSelector(state => state.notifications.toasts);

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          toast={toast}
          onClose={() => dispatch(hideToast(toast.id))}
        />
      ))}
    </div>
  );
};

const Toast = ({ toast, onClose }) => {
  const { type, message, duration = 5000 } = toast;

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getToastConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircleIcon,
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          iconColor: 'text-green-400',
          textColor: 'text-green-800',
        };
      case 'error':
        return {
          icon: XCircleIcon,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          iconColor: 'text-red-400',
          textColor: 'text-red-800',
        };
      case 'warning':
        return {
          icon: ExclamationTriangleIcon,
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          iconColor: 'text-yellow-400',
          textColor: 'text-yellow-800',
        };
      case 'info':
      default:
        return {
          icon: InformationCircleIcon,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          iconColor: 'text-blue-400',
          textColor: 'text-blue-800',
        };
    }
  };

  const config = getToastConfig();
  const Icon = config.icon;

  return (
    <div
      className={clsx(
        'relative flex items-start p-4 rounded-lg border shadow-lg transform transition-all duration-300 ease-in-out',
        config.bgColor,
        config.borderColor,
        'animate-slide-in-right'
      )}
    >
      <div className="flex-shrink-0">
        <Icon className={clsx('h-5 w-5', config.iconColor)} />
      </div>
      
      <div className="ml-3 flex-1">
        <p className={clsx('text-sm font-medium', config.textColor)}>
          {message}
        </p>
      </div>
      
      <div className="ml-4 flex-shrink-0 flex">
        <button
          className={clsx(
            'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2',
            config.textColor,
            'hover:bg-black hover:bg-opacity-10',
            'focus:ring-offset-2 focus:ring-indigo-500'
          )}
          onClick={onClose}
        >
          <span className="sr-only">Dismiss</span>
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ToastNotifications;